<!--
  NOTE: This is a placeholder for the @/components/Partner/PartnerPaymentRequestsTable component.
  The real one will be built with @/components/GraphqlDataTable/GraphqlDataTable.vue
-->
<template>
  <div class="card card-white">
    <div class="tw-flex tw-items-center tw-justify-between tw-py-2 tw-pr-2 tw-pl-2 tw-border-b tw-border-gray-200">
      <!-- Left side: Search and filter dropdown -->
      <div class="tw-flex tw-items-center tw-gap-3 tw-pl-0">
        <!-- Search input using WdsInput component -->
        <div class="tw-relative tw-w-[280px] tw-pl-0">
          <WdsInput
            v-model="searchQuery"
            class="tw-w-full tw-h-8 tw-text-sm tw-text-gray-900"
            type="text"
            prefix-icon="search"
            :placeholder="$t('Search name, PR ID, job title...')"
          >
            <WdsIcon
              v-if="searchQuery"
              :size="16"
              class="tw-cursor-pointer tw-text-gray-500"
              name="close-filled"
              @click="searchQuery = ''"
            />
          </WdsInput>
        </div>

        <!-- Filter button -->
        <div class="tw-relative">
          <PartnerFilterDropdown
            v-model="filters"
            :filter-groups="filterGroups"
            class="tw-text-sm tw-text-gray-900"
            @apply="applyFilters"
          />
        </div>
      </div>

      <!-- Right side: Company selector, Items Count, Sort, Export -->
      <div class="tw-flex tw-items-center">
        <!-- Company selector with icon -->
        <WdsSelect
          v-model="selectedCompany"
          class="tw-w-44 tw-h-8 tw-text-sm tw-text-gray-900"
          :list="companyOptions"
          :placeholder="$t('All companies')"
          data-test="company-selector"
          can-deselect
        >
          <template #prefix>
            <WdsIcon name="building" :size="16" class="tw-text-gray-400" />
          </template>
        </WdsSelect>

        <!-- Item count display -->
        <span class="tw-text-sm tw-text-gray-900 tw-mx-2">{{ filteredPaymentRequests.length }} items</span>

        <!-- Vertical divider -->
        <div class="vertical-divider tw-mx-2"></div>

        <!-- Sort button -->
        <button type="button" class="tw-flex tw-items-center tw-gap-1 tw-appearance-none tw-bg-transparent tw-border-0 tw-p-1 tw-cursor-pointer">
          <WdsIcon name="arrows" :size="16" class="tw-text-gray-900" weight="thick" />
          <span class="tw-text-sm tw-text-gray-900">{{ $t('Sort') }}</span>
        </button>

        <!-- Vertical divider -->
        <div class="vertical-divider tw-mx-2"></div>

        <!-- Export button, right-flushed -->
        <button type="button" class="tw-appearance-none tw-bg-transparent tw-border-0 tw-p-1 tw-cursor-pointer">
          <WdsIcon name="download" :size="16" class="tw-text-gray-900" />
        </button>
      </div>
    </div>

    <BaseDataTable
      :columns="columns"
      :entries="filteredPaymentRequests"
      tall-rows
      class="partner-table"
      :search-disabled="true"
      header-class="tw-font-medium tw-text-gray-600 tw-py-1 tw-text-sm"
      row-class="hover:tw-bg-gray-50 tw-text-sm"
    >
      <template #no-results>
        <div class="tw-justify-center" data-test="no-payment-requests-found">
          {{ $t('No payment requests found') }}
        </div>
      </template>

      <!-- Select column in header -->
      <template #head-select>
        <WdsCheckbox
          size="md"
          :checked="allSelected"
          :indeterminate="someSelected && !allSelected"
          @update:checked="toggleSelectAll"
          data-test="select-all-checkbox"
        />
      </template>

      <!-- Select column -->
      <template #select="{ row }">
        <WdsCheckbox
          size="md"
          :checked="selectedRows.includes(row.id)"
          @update:checked="(checked) => toggleSelectRow(row.id, checked)"
          data-test="row-checkbox"
        />
      </template>

      <!-- Client approval date column -->
      <template #ClientApprovalDate="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.clientApprovalDate }}</WdsText>
        </div>
      </template>

      <!-- PR ID column - Fixed to not add another hash -->
      <template #PRID="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText class="tw-font-medium">{{ row.prId }}</WdsText>
        </div>
      </template>

      <!-- Worker name and Freelancer ID -->
      <template #WorkerName="{ row }">
        <div class="tw-flex tw-flex-col tw-gap-0">
          <WdsText class="tw-font-medium">{{ row.worker.name }}</WdsText>
          <WdsText secondary>{{ row.worker.freelancerId }}</WdsText>
        </div>
      </template>

      <!-- Timesheet period column -->
      <template #TimesheetPeriod="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.timesheetPeriod }}</WdsText>
        </div>
      </template>

      <!-- Units column -->
      <template #Units="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.units }}</WdsText>
        </div>
      </template>

      <!-- Pay rate column -->
      <template #PayRate="{ row }">
        <div class="tw-flex tw-flex-col tw-gap-0">
          <WdsText class="tw-font-medium">USD {{ row.payRate.amount }}</WdsText>
          <WdsText secondary>{{ row.payRate.type }}</WdsText>
        </div>
      </template>

      <!-- Expenses column -->
      <template #Expenses="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.expenses ? `USD ${row.expenses}` : '-' }}</WdsText>
        </div>
      </template>

      <!-- Job title and ID column -->
      <template #JobTitle="{ row }">
        <div class="tw-flex tw-flex-col tw-gap-0">
          <WdsText class="tw-font-medium">{{ row.jobTitle }}</WdsText>
          <WdsText secondary>{{ row.jobId }}</WdsText>
        </div>
      </template>

      <!-- Client payment status -->
      <template #ClientPaymentStatus="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <span
            class="tw-py-1 tw-px-2 tw-rounded-md tw-text-xs tw-font-medium"
            :class="row.clientPaymentStatus === 'Paid' ? 'tw-bg-green-50 tw-text-green-700' : 'tw-bg-red-50 tw-text-red-700'"
          >
            {{ row.clientPaymentStatus }}
          </span>
        </div>
      </template>

      <!-- Assignment company -->
      <template #AssignmentCompany="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.assignmentCompany }}</WdsText>
        </div>
      </template>

      <!-- Work location -->
      <template #WorkLocation="{ row }">
        <div class="tw-flex tw-h-full tw-items-center">
          <WdsText>{{ row.workLocation }}</WdsText>
        </div>
      </template>

      <!-- Actions column -->
      <template #Actions>
        <WdsMenu placement="bottom-end">
          <template #trigger>
            <WdsButtonGhost
              :label="$t('More options')"
              data-test="payment-request-actions-menu"
              class="tw-p-1"
            >
              <WdsIcon name="overflow-menu-horizontal" />
            </WdsButtonGhost>
          </template>
          <WdsMenuOption @click="() => {}">
            {{ $t('View details') }}
          </WdsMenuOption>
          <WdsMenuOption @click="() => {}">
            {{ $t('Download') }}
          </WdsMenuOption>
        </WdsMenu>
      </template>
    </BaseDataTable>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { WdsButtonGhost, WdsCheckbox, WdsIcon, WdsInput, WdsMenu, WdsMenuOption, WdsText, WdsSelect } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import PartnerFilterDropdown from '@/components/Partner/PartnerFilterDropdown.vue'

// Define the FilterGroup type to match the PartnerFilterDropdown component's expected props
interface FilterOption {
  label: string;
  value: string | number | boolean;
}

interface FilterGroup {
  key: string;
  title: string;
  type: 'checkboxes' | 'select' | 'dateRange';
  options?: FilterOption[];
}

const props = defineProps<{
  paymentRequests: Array<{
    id: string;
    clientApprovalDate: string;
    prId: string;
    worker: {
      name: string;
      freelancerId: string;
    };
    timesheetPeriod: string;
    units: number;
    payRate: {
      amount: number;
      type: string; // 'hourly' or 'weekly'
    };
    expenses: number | null;
    jobTitle: string;
    jobId: string;
    clientPaymentStatus: 'Paid' | 'Not paid';
    assignmentCompany: string;
    workLocation: string;
  }>;
}>();

// Search functionality
const searchQuery = ref('');

// Filter functionality
interface FilterValues {
  clientPaymentStatus?: string[];
  companies?: string[];
  timesheetPeriod?: {
    from?: string;
    to?: string;
  };
  [key: string]: any;
}

const filters = ref<FilterValues>({});

// Filter groups definition for the filter dropdown
const filterGroups: FilterGroup[] = [
  {
    key: 'clientPaymentStatus',
    title: $t('Client payment status'),
    type: 'checkboxes',
    options: [
      { label: $t('Paid'), value: 'Paid' },
      { label: $t('Not paid'), value: 'Not paid' }
    ]
  },
  {
    key: 'companies',
    title: $t('Assignment Company'),
    type: 'select',
    options: [
      { label: 'Worksome Inc', value: 'Worksome Inc' },
      { label: 'Worksome UK Ltd', value: 'Worksome UK Ltd' },
      { label: 'Worksome ApS', value: 'Worksome ApS' }
    ]
  },
  {
    key: 'timesheetPeriod',
    title: $t('Timesheet period'),
    type: 'dateRange'
  },
];

// Filter payment requests based on filters
const applyFilters = () => {
  // The filtering will be applied via the filteredPaymentRequests computed property
};

// Company selector functionality
const companyOptions = [
  { label: 'Apple Inc.', value: 'Apple Inc.' },
  { label: 'FinTech Innovations', value: 'FinTech Innovations' },
  { label: 'Pitch Perfect Solutions', value: 'Pitch Perfect Solutions' },
];
const selectedCompany = ref('');

// Filter payment requests based on search query and filters
const filteredPaymentRequests = computed(() => {
  let results = props.paymentRequests;

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    results = results.filter(request => {
      // Search in worker name and freelancer ID
      if (request.worker.name.toLowerCase().includes(query) ||
          request.worker.freelancerId.toLowerCase().includes(query)) {
        return true;
      }

      // Search in job title and ID
      if (request.jobTitle.toLowerCase().includes(query) ||
          request.jobId.toLowerCase().includes(query)) {
        return true;
      }

      // Search in PR ID
      if (request.prId.toLowerCase().includes(query)) {
        return true;
      }

      // Search in assignment company
      if (request.assignmentCompany.toLowerCase().includes(query)) {
        return true;
      }

      // Search in work location
      if (request.workLocation.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }

  // Apply company filter
  if (selectedCompany.value) {
    results = results.filter(request =>
      request.assignmentCompany === selectedCompany.value);
  }

  // Apply filters
  if (filters.value.clientPaymentStatus && filters.value.clientPaymentStatus.length > 0) {
    results = results.filter(request =>
      filters.value.clientPaymentStatus?.includes(request.clientPaymentStatus));
  }

  if (filters.value.companies && filters.value.companies.length > 0) {
    results = results.filter(request =>
      filters.value.companies?.includes(request.assignmentCompany));
  }

  if (filters.value.timesheetPeriod) {
    // Note: This is a simplified filter for the demo. In a real implementation,
    // you would parse the timesheetPeriod string and compare it with the filter dates.
    const { from, to } = filters.value.timesheetPeriod;

    if (from || to) {
      results = results.filter(request => {
        // For demo purposes, we'll just check if the timesheetPeriod contains the date string
        // In a real implementation, you would parse the dates properly
        const period = request.timesheetPeriod.toLowerCase();
        const fromMatches = !from || period.includes(from.substring(0, 7).replace('-', '/'));
        const toMatches = !to || period.includes(to.substring(0, 7).replace('-', '/'));
        return fromMatches && toMatches;
      });
    }
  }

  return results;
});

// Table columns configuration
const columns = computed(() => [
  {
    key: 'select',
    title: '', // Checkbox column
    slot: 'select',
    orderAble: false,
    width: '40px',
    align: 'center',
    class: 'tw-py-0 tw-px-0 tw-text-center',
    colClass: 'tw-text-center',
  },
  {
    key: 'clientApprovalDate',
    title: $t('Client approval date'),
    slot: 'ClientApprovalDate',
    orderAble: true,
    width: '160px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'prId',
    title: $t('PR ID'),
    slot: 'PRID',
    orderAble: true,
    width: '100px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'worker.name',
    title: $t('Worker name, Freelancer ID'),
    slot: 'WorkerName',
    orderAble: true,
    width: '200px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'timesheetPeriod',
    title: $t('Timesheet period'),
    slot: 'TimesheetPeriod',
    orderAble: true,
    width: '160px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'units',
    title: $t('Units'),
    slot: 'Units',
    orderAble: true,
    width: '80px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'payRate',
    title: $t('Pay rate'),
    slot: 'PayRate',
    orderAble: true,
    width: '120px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'expenses',
    title: $t('Expenses'),
    slot: 'Expenses',
    orderAble: true,
    width: '100px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'jobTitle',
    title: $t('Job title and Worksome Job ID'),
    slot: 'JobTitle',
    orderAble: true,
    width: '200px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'clientPaymentStatus',
    title: $t('Client payment status'),
    slot: 'ClientPaymentStatus',
    orderAble: true,
    width: '140px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'assignmentCompany',
    title: $t('Assignment Company'),
    slot: 'AssignmentCompany',
    orderAble: true,
    width: '180px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'workLocation',
    title: $t('Work location'),
    slot: 'WorkLocation',
    orderAble: true,
    width: '180px',
    class: 'tw-py-2 tw-px-3',
  },
  {
    key: 'actions',
    title: $t('Actions'),
    slot: 'Actions',
    orderAble: false,
    width: '60px',
    sticky: true,
    class: 'tw-py-2 tw-px-2 tw-text-center',
    classes: 'tw-bg-white', // Add background color to sticky column
  },
]);

// Row selection functionality
const selectedRows = ref<string[]>([]);

const toggleSelectRow = (id: string, checked: boolean) => {
  if (checked) {
    selectedRows.value.push(id);
  } else {
    selectedRows.value = selectedRows.value.filter(rowId => rowId !== id);
  }
};

const toggleSelectAll = (checked: boolean) => {
  if (checked) {
    selectedRows.value = filteredPaymentRequests.value.map(row => row.id);
  } else {
    selectedRows.value = [];
  }
};

const allSelected = computed(() =>
  filteredPaymentRequests.value.length > 0 &&
  filteredPaymentRequests.value.every(row => selectedRows.value.includes(row.id))
);

const someSelected = computed(() =>
  selectedRows.value.length > 0 && !allSelected.value
);
</script>

<style scoped>
.partner-table :deep(tbody td) {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  vertical-align: middle;
}

.partner-table :deep(th) {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-weight: 500;
  vertical-align: middle;
}

/* Reduce spacing in stacked content */
.partner-table :deep(.wds-stack) {
  gap: 0.125rem !important;
}

/* Make elements more compact */
.partner-table :deep(.wds-text + .wds-text) {
  margin-top: 0;
  line-height: 1.2;
}

/* Vertical divider styling */
.vertical-divider {
  display: inline-block;
  height: 18px;
  border-right: 1px solid #d1d5db; /* gray-300 */
  vertical-align: middle;
}

/* Improve spacing for hierarchical content */
.partner-table :deep(.tw-flex-col) {
  gap: 0.125rem;
}

/* Adjust status indicators to be more compact */
.partner-table :deep(.tw-rounded-md) {
  padding: 0.25rem 0.5rem;
  line-height: 1;
}
</style>
